<template>
<v-dialog
  v-model="internalValue"
  max-width="900px"
  persistent
  scrollable
  content-class="new-diagnostic-dialog"
>
  <v-card class="rounded-lg" style="border-radius: 12px !important; overflow: hidden;">

    <!-- Header -->
    <v-card-title class="px-6 pt-5 pb-3" style="border-bottom: 1px solid #e0e0e0;">
      <div class="d-flex justify-space-between align-start w-100">
        <div>
          <h2 class="text-h6 font-weight-bold grey--text text--darken-3 mb-1">
            Новая диагностика
          </h2>
          <div class="text-body-2" style="color: #009E60;">
            Соберите диагностику как конструктор: компетенции, кейсы, модули отчёта и порядок этапов.
          </div>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <!-- Tabs -->
    <div class="px-6 pt-4" style="border-bottom: 1px solid #e0e0e0;">
      <div class="d-flex" style="gap: 4px;">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
          <span v-if="tab.key === 'competencies' && selectedCompetencies.length" class="tab-count">
              {{ selectedCompetencies.length }}
            </span>
          <span v-if="tab.key === 'cases' && selectedCases.length" class="tab-count">
              {{ selectedCases.length }}
            </span>
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <v-card-text class="px-6 py-5" style="max-height: 520px; overflow-y: auto;">

      <!-- Основное -->
      <div v-if="activeTab === 'basic'">
        <v-row>
          <v-col cols="12" sm="6">
            <div class="field-label">ФИО участника</div>
            <v-autocomplete
              v-model="form.candidate"
              :items="candidateItems"
              item-value="id"
              :item-text="candidateFullName"
              :loading="candidatesLoading.load"
              dense
              outlined
              hide-details="auto"
              :error-messages="errors.candidate"
              clearable
              no-filter
              no-data-text="Не найдено"
              placeholder="Начните вводить имя..."
              class="rounded-lg"
              @input="onCandidateInput"
              @update:search-input="onCandidateSearch"
            />
            <div class="field-label mt-3">Или укажите ФИО вручную</div>
            <v-text-field
              v-model="form.candidate_name"
              dense
              outlined
              hide-details="auto"
              :error-messages="errors.candidate_name"
              placeholder="ФИО участника, если его нет в списке"
              class="rounded-lg"
              @input="onCandidateNameInput"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <div class="field-label">Компания</div>
            <v-text-field
              v-model="form.company"
              dense
              outlined
              hide-details="auto"
              :error-messages="errors.company"
              class="rounded-lg"
              @input="errors.company = ''"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <div class="field-label">Должность</div>
            <v-text-field
              v-model="form.position"
              dense
              outlined
              hide-details="auto"
              :error-messages="errors.position"
              class="rounded-lg"
              @input="errors.position = ''"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <div class="field-label">Эксперт</div>
            <v-autocomplete
              v-model="form.manager"
              :items="managerItems"
              item-value="id"
              :item-text="managerFullName"
              :loading="managersLoading.load"
              dense
              outlined
              hide-details="auto"
              :error-messages="errors.manager"
              clearable
              no-filter
              no-data-text="Не найдено"
              placeholder="Начните вводить имя..."
              class="rounded-lg"
              @input="onManagerInput"
              @update:search-input="onManagerSearch"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Компетенции -->
      <div v-else-if="activeTab === 'competencies'">

        <!-- Быстрые пресеты под роль -->
        <v-card class="rounded-lg mb-4 pa-4" elevation="0" style="border: 1px solid #e0e0e0;">
          <div class="d-flex align-center mb-3" style="gap: 8px;">
            <v-icon small color="grey darken-1">mdi-lightning-bolt</v-icon>
            <span class="text-caption font-weight-bold grey--text text--darken-2"
                  style="letter-spacing: 0.05em; text-transform: uppercase;">
                Быстрые пресеты под роль
              </span>
          </div>
          <div class="d-flex flex-wrap" style="gap: 8px;">
            <v-tooltip
              v-for="preset in presetsRoles"
              :key="preset.id"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  label
                  class="preset-chip"
                  :class="{ 'preset-chip--active': activePresetId === preset.id }"
                  @click="applyPreset(preset)"
                >
                  {{ preset.name }}
                  <span class="preset-count">{{ preset.competences ? preset.competences.length : 0 }}</span>
                </v-chip>
              </template>
              <span>{{ preset.description || preset.name }}</span>
            </v-tooltip>
          </div>
        </v-card>

        <!-- Ошибка компетенций -->
        <v-alert
          v-if="errors.competencies"
          type="error"
          dense
          text
          class="mb-3 rounded-lg"
          style="font-size: 13px;"
        >
          {{ errors.competencies }}
        </v-alert>

        <!-- Библиотека компетенций -->
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center" style="gap: 6px;">
            <v-icon small color="grey darken-1">mdi-view-list</v-icon>
            <span class="text-caption font-weight-bold grey--text text--darken-2"
                  style="letter-spacing: 0.05em; text-transform: uppercase;">
                Библиотека компетенций
              </span>
            <span class="text-caption grey--text">
                {{ selectedCompetencies.length }}/{{ libraryCompetencies.length }}
              </span>
          </div>
          <v-btn
            text
            small
            color="grey darken-1"
            class="text-capitalize"
            @click="openLibrary"
          >
            <v-icon small left>mdi-tune</v-icon>
            Управлять библиотекой
          </v-btn>
        </div>

        <!-- Фильтры -->
        <div class="d-flex mb-3" style="gap: 8px;">
          <v-text-field
            v-model="competencySearch"
            dense
            outlined
            hide-details
            placeholder="Поиск компетенций"
            prepend-inner-icon="mdi-magnify"
            class="flex-grow-1 rounded-lg"
          />
          <v-select
            v-model="categoryFilter"
            :items="categoryOptions"
            dense
            outlined
            hide-details
            style="max-width: 180px;"
            class="rounded-lg"
          />
          <v-select
            v-model="levelFilter"
            :items="levelOptions"
            dense
            outlined
            hide-details
            style="max-width: 160px;"
            class="rounded-lg"
          />
        </div>

        <!-- Карточки компетенций -->
        <v-row v-if="filteredCompetencies.length">
          <v-col
            v-for="competency in filteredCompetencies"
            :key="competency.id"
            cols="12" sm="6"
          >
            <div
              class="competency-card"
              :class="{ 'competency-card--selected': isSelected(competency.id) }"
              @click="toggleCompetency(competency.id)"
            >
              <div class="d-flex align-start justify-space-between">
                <div class="flex-grow-1 mr-2">
                  <div class="d-flex align-center mb-1" style="gap: 6px;">
                    <v-icon
                      v-if="isSelected(competency.id)"
                      small
                      color="#009E60"
                    >
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-else small color="#c0c0c0">mdi-circle-outline</v-icon>
                    <span class="font-weight-bold text-body-2 grey--text text--darken-3">
                        {{ competency.name }}
                      </span>
                    <v-chip
                      label
                      x-small
                      style="background-color: #f3f4f6; color: #6b7280; height: 20px; font-size: 10px;"
                    >
                      {{ competency.category }}
                    </v-chip>
                  </div>
                  <p class="text-caption grey--text mb-2" style="line-height: 1.4; padding-left: 22px;">
                    {{ competency.description }}
                  </p>
                  <div class="d-flex flex-wrap" style="gap: 4px; padding-left: 22px;">
                    <v-chip
                      v-for="level in competency.level_tags"
                      :key="level"
                      label
                      x-small
                      style="background-color: #f3f4f6; color: #483CB9; height: 20px; font-size: 10px;"
                    >
                      {{ level }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
        <div v-else class="text-center grey--text py-6 text-body-2">
          Компетенции не найдены
        </div>
      </div>

      <!-- Кейсы -->
      <div v-else-if="activeTab === 'cases'">

        <!-- Нет выбранных компетенций -->
        <v-alert
          v-if="!selectedCompetencies.length"
          type="info"
          text
          dense
          class="mb-4 rounded-lg"
          style="font-size: 13px;"
        >
          Выберите компетенции на вкладке «Компетенции», чтобы добавить дополнительные кейсы.
        </v-alert>

        <template v-else>
          <!-- Фильтры -->
          <div class="d-flex mb-3" style="gap: 8px;">
            <v-text-field
              v-model="casesSearch"
              dense
              outlined
              hide-details
              placeholder="Поиск кейсов"
              prepend-inner-icon="mdi-magnify"
              class="flex-grow-1 rounded-lg"
            />
            <v-select
              v-model="caseCompetenceFilter"
              :items="caseCompetenceOptions"
              item-text="name"
              item-value="id"
              dense
              outlined
              hide-details
              style="max-width: 220px;"
              class="rounded-lg"
            />
          </div>

          <!-- Загрузка -->
          <div v-if="libraryCasesLoading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="#009E60"/>
          </div>

          <!-- Список кейсов -->
          <div v-else-if="filteredCasesForModal.length" class="cases-list">
            <div
              v-for="c in filteredCasesForModal"
              :key="c.id"
              class="case-row"
              :class="{ 'case-row--selected': isCaseSelected(c.id) }"
              @click="toggleCase(c.id)"
            >
              <div class="d-flex align-center" style="gap: 8px;">
                <v-icon :color="isCaseSelected(c.id) ? '#009E60' : '#c0c0c0'" small>
                  {{ isCaseSelected(c.id) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
                <span class="font-weight-medium text-body-2 grey--text text--darken-3">{{ c.name }}</span>
                <v-chip
                  label
                  x-small
                  style="background-color: #f3f4f6; color: #6b7280; height: 20px; font-size: 10px;"
                >
                  {{ competenceNameForCase(c.competence) }}
                </v-chip>
              </div>
              <p v-if="c.description" class="text-caption grey--text mt-1 mb-0" style="padding-left: 28px; line-height: 1.4;">
                {{ c.description }}
              </p>
            </div>
          </div>

          <div v-else class="text-center grey--text py-6 text-body-2">
            Кейсы не найдены
          </div>
        </template>
      </div>

      <!-- Отчёт -->
      <div v-else-if="activeTab === 'report'">
        <div class="d-flex align-center mb-4" style="gap: 8px;">
          <v-icon small color="grey darken-1">mdi-file-document-outline</v-icon>
          <span class="text-caption font-weight-bold grey--text text--darken-2" style="letter-spacing: 0.05em; text-transform: uppercase;">
              Что выводить в отчёт
            </span>
        </div>
        <v-row>
          <v-col
            v-for="module in reportModules"
            :key="module.key"
            cols="12" sm="6"
          >
            <div
              class="report-module-card"
              :class="{ 'report-module-card--selected': isReportModuleSelected(module.key) }"
              @click="toggleReportModule(module.key)"
            >
              <div class="d-flex align-start" style="gap: 10px;">
                <v-icon
                  :color="isReportModuleSelected(module.key) ? '#009E60' : '#c0c0c0'"
                  style="margin-top: 1px; flex-shrink: 0;"
                >
                  {{ isReportModuleSelected(module.key) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
                <div>
                  <div class="font-weight-bold text-body-2 grey--text text--darken-3">{{ module.label }}</div>
                  <div class="text-caption grey--text" style="line-height: 1.4;">{{ module.description }}</div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Этапы — заглушка -->
      <div v-else class="d-flex flex-column align-center justify-center py-10">
        <v-icon size="48" color="grey lighten-1">mdi-clock-outline</v-icon>
        <div class="text-body-2 grey--text mt-3">Этот раздел будет доступен позже</div>
      </div>

    </v-card-text>

    <!-- Footer -->
    <v-divider/>
    <v-card-actions class="px-6 py-4 d-flex justify-end" style="gap: 12px;">
      <v-btn
        text
        color="grey darken-2"
        class="text-capitalize px-5"
        @click="close"
      >
        Отмена
      </v-btn>
      <v-btn
        color="#009E60"
        dark
        depressed
        class="text-capitalize px-6"
        height="40"
        :loading="loading"
        @click="submit"
      >
        Создать диагностику
      </v-btn>
    </v-card-actions>

  </v-card>
</v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

const LEVEL_OPTIONS = ['Junior', 'Middle', 'Senior', 'Top'];

export default {
  name: 'NewDiagnosticModal',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      activeTab: 'basic',
      candidateTimer: null,
      managerTimer: null,
      skipCandidateSearch: false,
      skipManagerSearch: false,
      savedCandidate: null,
      savedManager: null,
      errors: {
        candidate: '',
        candidate_name: '',
        manager: '',
        company: '',
        position: '',
        competencies: '',
      },
      tabs: [
        {
          key: 'basic',
          label: 'Основное',
        },
        {
          key: 'competencies',
          label: 'Компетенции',
        },
        {
          key: 'cases',
          label: 'Кейсы',
        },
        {
          key: 'report',
          label: 'Отчёт',
        },
        {
          key: 'stages',
          label: 'Этапы',
        },
      ],
      form: {
        candidate: null,
        candidate_name: '',
        manager: null,
        company: '',
        position: '',
      },
      selectedCompetencies: [],
      activePresetId: null,
      competencySearch: '',
      categoryFilter: 'Все категории',
      levelFilter: 'Все уровни',
      selectedCases: [],
      casesSearch: '',
      caseCompetenceFilter: null,
      selectedReportModules: [
        'show_comments',
        'show_graphs',
        'show_idp',
        'show_conclusion',
        'show_result_notes',
        'show_competence_notes',
      ],
    };
  },

  computed: {
    ...mapGetters({
      libraryCompetencies: 'diagnostics/diagnotics/libraryCompetencies',
      presetsRoles: 'diagnostics/diagnotics/presetsRoles',
      libraryCases: 'diagnostics/diagnotics/libraryCases',
      libraryCasesLoading: 'diagnostics/diagnotics/libraryCasesLoading',
      reportModules: 'diagnostics/diagnotics/reportModules',
      candidateOptions: 'users/candidates/candidates',
      candidatesLoading: 'users/candidates/loading',
      managerOptions: 'users/managers/managers',
      managersLoading: 'users/managers/loading',
    }),

    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },

    candidateItems() {
      const items = [...(this.candidateOptions || [])];
      if (this.savedCandidate && !items.find(c => c.id === this.savedCandidate.id)) {
        items.unshift(this.savedCandidate);
      }
      return items;
    },

    managerItems() {
      const items = [...(this.managerOptions || [])];
      if (this.savedManager && !items.find(m => m.id === this.savedManager.id)) {
        items.unshift(this.savedManager);
      }
      return items;
    },

    categoryOptions() {
      const cats = new Set(this.libraryCompetencies.map(c => c.category)
        .filter(Boolean));
      return ['Все категории', ...Array.from(cats)];
    },

    levelOptions() {
      return ['Все уровни', ...LEVEL_OPTIONS];
    },

    filteredCompetencies() {
      return this.libraryCompetencies.filter(c => {
        const matchSearch = !this.competencySearch
          || c.name.toLowerCase()
            .includes(this.competencySearch.toLowerCase())
          || (c.description && c.description.toLowerCase()
            .includes(this.competencySearch.toLowerCase()));

        const matchCategory = this.categoryFilter === 'Все категории'
          || c.category === this.categoryFilter;

        const matchLevel = this.levelFilter === 'Все уровни'
          || (c.level_tags && c.level_tags.includes(this.levelFilter));

        return matchSearch && matchCategory && matchLevel;
      });
    },

    caseCompetenceOptions() {
      const selected = this.libraryCompetencies.filter(c => this.selectedCompetencies.includes(c.id));
      return [
        {
          id: null,
          name: 'Все компетенции',
        },
        ...selected.map(c => ({
          id: c.id,
          name: c.name,
        })),
      ];
    },

    filteredCasesForModal() {
      return this.libraryCases.filter(c => {
        const inSelected = this.selectedCompetencies.includes(c.competence);
        if (!inSelected) return false;

        const matchComp = this.caseCompetenceFilter === null
          || c.competence === this.caseCompetenceFilter;

        const matchSearch = !this.casesSearch
          || c.name.toLowerCase()
            .includes(this.casesSearch.toLowerCase())
          || (c.description && c.description.toLowerCase()
            .includes(this.casesSearch.toLowerCase()));

        return matchComp && matchSearch;
      });
    },
  },

  watch: {
    value(v) {
      if (v) {
        this.reset();
        this.loadCandidates();
        this.loadManagersFilter();
        if (!this.libraryCompetencies.length) this.loadCompetenciesLibrary();
        if (!this.presetsRoles.length) this.loadPresetsRoles();
        if (!this.libraryCases.length) this.loadCasesLibrary();
      }
    },
  },

  methods: {
    ...mapActions({
      createChecklist: 'diagnostics/diagnotics/createChecklist',
      loadCandidates: 'users/candidates/loadCandidates',
      setCandidateFilterName: 'users/candidates/setFilterName',
      loadManagersFilter: 'users/managers/loadManagersFilter',
      setManagerFilterName: 'users/managers/setFilterName',
      loadCompetenciesLibrary: 'diagnostics/diagnotics/loadCompetenciesLibrary',
      loadPresetsRoles: 'diagnostics/diagnotics/loadPresetsRoles',
      loadCasesLibrary: 'diagnostics/diagnotics/loadCasesLibrary',
    }),
    ...mapMutations({
      SET_COMPETENCIES_MODAL: 'diagnostics/diagnotics/SET_COMPETENCIES_MODAL',
    }),

    candidateFullName(c) {
      return `${c.last_name || ''} ${c.first_name || ''}`.trim();
    },

    managerFullName(m) {
      return `${m.last_name || ''} ${m.first_name || ''}`.trim();
    },

    onCandidateInput(val) {
      if (val) {
        this.skipCandidateSearch = true;
        this.savedCandidate = this.candidateOptions.find(c => c.id === val) || null;
        this.errors.candidate = '';
        this.form.candidate_name = '';
      } else {
        this.savedCandidate = null;
      }
    },

    onCandidateNameInput() {
      if (this.form.candidate_name && this.form.candidate_name.trim()) {
        this.form.candidate = null;
        this.savedCandidate = null;
      }
      this.errors.candidate_name = '';
    },

    onManagerInput(val) {
      if (val) {
        this.skipManagerSearch = true;
        this.savedManager = this.managerOptions.find(m => m.id === val) || null;
        this.errors.manager = '';
      } else {
        this.savedManager = null;
      }
    },

    onCandidateSearch(val) {
      // null приходит при blur — игнорируем, чтобы выбранный элемент не пропал
      if (val === null || val === undefined) return;
      if (this.skipCandidateSearch) {
        this.skipCandidateSearch = false;
        return;
      }
      clearTimeout(this.candidateTimer);
      this.candidateTimer = setTimeout(() => {
        this.setCandidateFilterName(val);
      }, 400);
    },

    onManagerSearch(val) {
      if (val === null || val === undefined) return;
      if (this.skipManagerSearch) {
        this.skipManagerSearch = false;
        return;
      }
      clearTimeout(this.managerTimer);
      this.managerTimer = setTimeout(() => {
        this.setManagerFilterName(val);
      }, 400);
    },

    isSelected(id) {
      return this.selectedCompetencies.includes(id);
    },

    toggleCompetency(id) {
      const idx = this.selectedCompetencies.indexOf(id);
      if (idx === -1) {
        this.selectedCompetencies.push(id);
        this.autoSelectCasesForCompetency(id);
      } else {
        this.selectedCompetencies.splice(idx, 1);
        this.autoDeselectCasesForCompetency(id);
      }
      this.activePresetId = null;
      if (this.selectedCompetencies.length) this.errors.competencies = '';
    },

    autoSelectCasesForCompetency(competencyId) {
      const cases = this.libraryCases.filter(c => c.competence === competencyId);
      cases.forEach(c => {
        if (!this.selectedCases.includes(c.id)) this.selectedCases.push(c.id);
      });
    },

    autoDeselectCasesForCompetency(competencyId) {
      const caseIds = this.libraryCases.filter(c => c.competence === competencyId)
        .map(c => c.id);
      this.selectedCases = this.selectedCases.filter(id => !caseIds.includes(id));
    },

    validate() {
      this.errors = {
        candidate: '',
        candidate_name: '',
        company: '',
        position: '',
        manager: '',
        competencies: '',
      };
      let valid = true;

      if (!this.form.candidate && !this.form.candidate_name?.trim()) {
        this.errors.candidate = 'Укажите участника из списка или введите ФИО вручную';
        this.errors.candidate_name = 'Укажите участника из списка или введите ФИО вручную';
        valid = false;
      }
      if (!this.form.company?.trim()) {
        this.errors.company = 'Обязательное поле';
        valid = false;
      }
      if (!this.form.position?.trim()) {
        this.errors.position = 'Обязательное поле';
        valid = false;
      }
      if (!this.form.manager) {
        this.errors.manager = 'Обязательное поле';
        valid = false;
      }
      if (!this.selectedCompetencies.length) {
        this.errors.competencies = 'Выберите хотя бы одну компетенцию';
        valid = false;
      }

      const basicInvalid = this.errors.candidate || this.errors.candidate_name || this.errors.company || this.errors.position || this.errors.manager;
      if (basicInvalid) {
        this.activeTab = 'basic';
      } else if (this.errors.competencies) {
        this.activeTab = 'competencies';
      }

      return valid;
    },

    applyPreset(preset) {
      if (this.activePresetId === preset.id) {
        this.activePresetId = null;
        this.selectedCompetencies = [];
        this.selectedCases = [];
        return;
      }
      this.activePresetId = preset.id;
      const ids = (preset.competences || []).map(c => (typeof c === 'object' ? c.id : c));
      this.selectedCompetencies = [...ids];
      this.selectedCases = this.libraryCases
        .filter(c => ids.includes(c.competence))
        .map(c => c.id);
      if (ids.length) this.errors.competencies = '';
    },

    openLibrary() {
      this.SET_COMPETENCIES_MODAL(true);
    },

    isCaseSelected(id) {
      return this.selectedCases.includes(id);
    },

    toggleCase(id) {
      const idx = this.selectedCases.indexOf(id);
      if (idx === -1) {
        this.selectedCases.push(id);
      } else {
        this.selectedCases.splice(idx, 1);
      }
    },

    isReportModuleSelected(key) {
      return this.selectedReportModules.includes(key);
    },

    toggleReportModule(key) {
      const idx = this.selectedReportModules.indexOf(key);
      if (idx === -1) {
        this.selectedReportModules.push(key);
      } else {
        this.selectedReportModules.splice(idx, 1);
      }
    },

    competenceNameForCase(competenceId) {
      const comp = this.libraryCompetencies.find(c => c.id === competenceId);
      return comp ? comp.name : '';
    },

    reset() {
      this.activeTab = 'basic';
      this.form = {
        candidate: null,
        candidate_name: '',
        manager: null,
        company: '',
        position: '',
      };
      this.selectedCompetencies = [];
      this.activePresetId = null;
      this.competencySearch = '';
      this.categoryFilter = 'Все категории';
      this.levelFilter = 'Все уровни';
      this.selectedCases = [];
      this.casesSearch = '';
      this.caseCompetenceFilter = null;
      this.selectedReportModules = [
        'show_comments',
        'show_graphs',
        'show_idp',
        'show_conclusion',
        'show_result_notes',
        'show_competence_notes',
      ];
      this.savedCandidate = null;
      this.savedManager = null;
      this.errors = {
        candidate: '',
        candidate_name: '',
        company: '',
        position: '',
        manager: '',
        competencies: '',
      };
    },

    close() {
      this.$emit('input', false);
    },

    async submit() {
      if (this.loading) return;
      if (!this.validate()) return;
      this.loading = true;
      try {
        const competenciesFull = this.selectedCompetencies
          .map(id => this.libraryCompetencies.find(c => c.id === id))
          .filter(Boolean);
        const casesFull = this.selectedCases
          .map(id => this.libraryCases.find(c => c.id === id))
          .filter(Boolean);
        const payload = {
          ...this.form,
          candidate: this.form.candidate || null,
          candidate_name: (this.form.candidate_name || '').trim() || null,
          competencies: competenciesFull,
          cases: casesFull,
          selectedReportModuleKeys: this.selectedReportModules,
        };
        const checklist = await this.createChecklist(payload);
        this.$emit('created', checklist);
        this.close();
      } catch {
        // уведомление об ошибке выводит store
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.new-diagnostic-dialog {
  max-height: 90vh;
}
</style>

<style scoped lang="scss">
.w-100 {
  width: 100%;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

/* Tabs */
.tab-btn {
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
  border-radius: 8px 8px 0 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s;

  &:hover {
    color: #374151;
  }

  &--active {
    font-weight: 600;
    color: #111827;
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-bottom: 2px solid #fff;
    margin-bottom: -1px;
  }
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  background-color: #009E60;
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

/* Preset chips */
.preset-chip {
  background-color: #f3f4f6 !important;
  color: #374151 !important;
  cursor: pointer;
  transition: background-color 0.15s;
  display: flex !important;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #e5e7eb !important;
  }

  &--active {
    background-color: #d1fae5 !important;
    color: #065f46 !important;
    border: 1px solid #009E60 !important;
  }
}

.preset-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 9px;
  font-size: 10px;
  font-weight: 600;
}

.preset-chip--active .preset-count {
  background-color: rgba(0, 158, 96, 0.25);
  color: #065f46;
}

/* Competency cards */
.competency-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  background: #fff;

  &:hover {
    border-color: #009E60;
    background-color: #f0fdf8;
  }

  &--selected {
    border-color: #009E60;
    background-color: #f0fdf8;
  }
}

/* Report module cards */
.report-module-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  background: #fff;
  height: 100%;

  &:hover {
    border-color: #009E60;
    background-color: #f0fdf8;
  }

  &--selected {
    border-color: #009E60;
    background-color: #f0fdf8;
  }
}

/* Case rows */
.cases-list {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.case-row {
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.12s;
  background: #fff;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f0fdf8;
  }

  &--selected {
    background-color: #f0fdf8;
    border-left: 3px solid #009E60;
    padding-left: 11px;
  }
}
</style>
